
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTimeline } from "./useTimeline";
import { useSmartReassignment } from "./useSmartReassignment";
import { useProfileSwitch } from "@/contexts/ProfileSwitchContext";
import { useEffect } from "react";

interface ProfileData {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  birthdate?: string;
  referral_code?: string;
}

export const useTimelineAccess = (username?: string) => {
  const { user } = useAuth();
  const { activeProfile } = useProfileSwitch();
  const { smartReassign, forceReassign } = useSmartReassignment();
  
  // Determine if this is the user's own timeline or active profile timeline
  const isOwnProfile = !username;
  const isActiveProfile = activeProfile && !username;
  
  // Fetch profile data - either for the requested username, active profile, or current user
  const { data: profileData, isLoading: profileLoading, error: profileError } = useQuery({
    queryKey: ['profile-for-timeline', username || activeProfile?.id || 'me'],
    queryFn: async () => {
      if (isActiveProfile && activeProfile) {
        // For active profile, use the data from context
        return {
          id: activeProfile.id,
          username: activeProfile.username,
          first_name: activeProfile.first_name,
          last_name: activeProfile.last_name,
          birthdate: activeProfile.birthdate,
          referral_code: activeProfile.referral_code
        } as ProfileData;
      } else if (isOwnProfile && !activeProfile) {
        // For own profile, get data from current user
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name, birthdate, referral_code')
          .eq('id', user.id)
          .maybeSingle();
        
        if (error) {
          console.error('Own profile fetch error:', error);
          throw new Error(`Database error: ${error.message}`);
        }
        
        if (!data) {
          throw new Error('Your profile was not found');
        }
        
        return data as ProfileData;
      } else {
        // For friend's profile, get data by username
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name, birthdate, referral_code')
          .eq('username', username)
          .maybeSingle();
        
        if (error) {
          console.error('Profile fetch error:', error);
          throw new Error(`Database error: ${error.message}`);
        }
        
        if (!data) {
          throw new Error(`Profile not found for username: ${username}`);
        }
        
        return data as ProfileData;
      }
    },
    enabled: isActiveProfile ? !!activeProfile : (isOwnProfile ? !!user : !!username),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  // Check friendship status using enhanced access function
  const { data: friendshipData, isLoading: friendshipLoading } = useQuery({
    queryKey: ['timeline-access-check', user?.id, profileData?.id],
    queryFn: async () => {
      if (!user?.id || !profileData?.id) {
        return { canAccess: false };
      }

      // Use the enhanced access function which handles ownership and friendship
      const { data, error } = await supabase.rpc('can_access_timeline_enhanced', {
        target_user_id: profileData.id
      });

      if (error) {
        console.error('Timeline access check error:', error);
        return { canAccess: false };
      }

      return { canAccess: data };
    },
    enabled: !!user?.id && !!profileData?.id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  // Get timeline data using the enhanced useTimeline hook with birthdate dependency
  const { 
    timelineSections, 
    isLoading: timelineLoading, 
    error: timelineError, 
    timelineExists 
  } = useTimeline(profileData?.id || '', profileData?.birthdate);

  // Smart reassignment - only when birthdate actually changes
  useEffect(() => {
    const shouldReassign = (isOwnProfile && !activeProfile) || isActiveProfile;
    
    if (profileData?.id && profileData?.birthdate && shouldReassign && timelineExists) {
      smartReassign({
        userId: profileData.id,
        birthdate: profileData.birthdate,
        shouldReassign,
        timelineExists
      });
    }
  }, [profileData?.id, profileData?.birthdate, isOwnProfile, isActiveProfile, timelineExists, smartReassign, activeProfile]);

  // Determine access permissions
  const canView = (isOwnProfile || isActiveProfile) || (friendshipData?.canAccess === true);
  const needsTimelineSetup = !timelineExists && (isOwnProfile || isActiveProfile);

  // Combine loading states
  const isLoading = profileLoading || (profileData?.id ? friendshipLoading || timelineLoading : false);
  
  // Handle errors
  let error: string | null = null;
  if (profileError) {
    error = profileError.message;
  } else if (timelineError && profileData?.id && canView) {
    error = timelineError;
  } else if (!canView && !isOwnProfile && !isActiveProfile && profileData?.id && !friendshipLoading) {
    error = 'Access denied: You need to be friends to view this timeline';
  }

  return {
    profileData,
    timelineSections,
    isLoading,
    error,
    needsTimelineSetup,
    canView,
    isOwnProfile: isOwnProfile || isActiveProfile,
    // Manual reassignment for user-initiated actions
    triggerReassignment: (force = false) => {
      if (profileData?.id && profileData?.birthdate) {
        if (force) {
          forceReassign(profileData.id, profileData.birthdate);
        } else {
          smartReassign({
            userId: profileData.id,
            birthdate: profileData.birthdate,
            shouldReassign: true,
            timelineExists: !!timelineExists,
            force: false
          });
        }
      }
    }
  };
};
